import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../config/constants';
const PRIMARY_COLOR = '#6366f1';
const SECONDARY_COLOR = '#4f46e5';

// Icônes SVG simples
const SyncIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z" />
    </svg>
);

const CheckCircleIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
    </svg>
);

const PlayCircleIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" />
    </svg>
);

function LipSyncManager({ transcript, projectId, onTranscriptUpdate, onSave, onProcessingChange, onVideoReload }) {
    const [selectedSegmentIndices, setSelectedSegmentIndices] = useState(new Set());
    const [processingSegments, setProcessingSegments] = useState(new Map());
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('info');
    const [overallProgress, setOverallProgress] = useState(0);

    // Filtrer les segments qui contiennent à la fois audio et vidéo
    // Mémoriser les segments filtrés pour éviter les recalculs
    const videoAudioSegments = React.useMemo(() => {
        return transcript.filter(segment =>
            (segment.video_segment_file || segment.video_file) &&
            segment.segment_file &&
            !segment.is_silent
        );
    }, [transcript]); // Recalculer seulement si transcript change

    useEffect(() => {
        if (onProcessingChange) {
            onProcessingChange(processingSegments, videoAudioSegments);
        }
    }, [processingSegments, videoAudioSegments, onProcessingChange]);


    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Fonction pour tronquer le texte intelligemment
    const truncateText = (text, maxLength = 40) => {
        if (!text || text.length <= maxLength) return text;

        // Trouve le dernier espace avant la limite
        const truncated = text.substring(0, maxLength);
        const lastSpaceIndex = truncated.lastIndexOf(' ');

        // Si on trouve un espace, on coupe là, sinon on coupe à la limite
        const cutIndex = lastSpaceIndex > maxLength * 0.7 ? lastSpaceIndex : maxLength;

        return text.substring(0, cutIndex).trim() + '...';
    };

    const handleSegmentToggle = (index) => {
        if (processingSegments.size > 0) return; // Empêcher la sélection pendant le traitement

        const newSelected = new Set(selectedSegmentIndices);
        if (newSelected.has(index)) {
            newSelected.delete(index);
        } else {
            newSelected.add(index);
        }
        setSelectedSegmentIndices(newSelected);
    };

    const handleSelectAll = () => {
        if (processingSegments.size > 0) return;

        if (selectedSegmentIndices.size === videoAudioSegments.length) {
            setSelectedSegmentIndices(new Set());
        } else {
            const allIndices = new Set(videoAudioSegments.map((_, index) => index));
            setSelectedSegmentIndices(allIndices);
        }
    };

    const processSegment = async (segment, index) => {
        // Mise à jour de l'état de traitement pour ce segment
        setProcessingSegments(prev => new Map(prev).set(index, { progress: 20, status: 'processing' }));

        // Simulation de progression
        const progressInterval = setInterval(() => {
            setProcessingSegments(prev => {
                const newMap = new Map(prev);
                const current = newMap.get(index);
                if (current && current.progress < 80) {
                    newMap.set(index, { ...current, progress: current.progress + 10 });
                }
                return newMap;
            });
        }, 500);

        try {
            const response = await fetch(`${API_CONFIG.BASE_URL}/lipsync_segment/${projectId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ segment }),
            });

            clearInterval(progressInterval);

            if (!response.ok) {
                throw new Error('Failed to process lip sync');
            }

            const result = await response.json();

            // Mise à jour réussie
            setProcessingSegments(prev => {
                const newMap = new Map(prev);
                newMap.set(index, { progress: 100, status: 'success' });
                return newMap;
            });

            return result;
        } catch (error) {
            clearInterval(progressInterval);
            // Mise à jour en cas d'erreur
            setProcessingSegments(prev => {
                const newMap = new Map(prev);
                newMap.set(index, { progress: 0, status: 'error' });
                return newMap;
            });
            throw error;
        }
    };
    const handleLipSync = async () => {
        if (selectedSegmentIndices.size === 0) return;

        setMessage(`🎬 Processing ${selectedSegmentIndices.size} segment${selectedSegmentIndices.size > 1 ? 's' : ''}...`);
        setMessageType('info');

        const selectedSegments = Array.from(selectedSegmentIndices).map(index => ({
            segment: videoAudioSegments[index],
            index
        }));

        // Lancer tous les traitements en parallèle
        const promises = selectedSegments.map(({ segment, index }) =>
            processSegment(segment, index)
        );

        try {
            const results = await Promise.allSettled(promises);

            // Compter les succès et les échecs
            const successCount = results.filter(r => r.status === 'fulfilled').length;
            const failCount = results.filter(r => r.status === 'rejected').length;

            // Mettre à jour le transcript si nécessaire
            const successfulResults = results
                .filter(r => r.status === 'fulfilled')
                .map(r => r.value)
                .filter(r => r && r.updatedTranscript);

            if (successfulResults.length > 0) {
                // Utiliser le dernier transcript mis à jour
                const lastResult = successfulResults[successfulResults.length - 1];
                onTranscriptUpdate(lastResult.updatedTranscript);
                await onSave(lastResult.updatedTranscript);

                // ✅ NOUVEAU : Forcer le reload vidéo pour tous les segments traités avec succès
                if (onVideoReload) {
                    successfulResults.forEach((result, index) => {
                        if (result.updatedSegment) {
                            console.log(`LipSyncManager: Triggering video reload for segment:`, result.updatedSegment.video_segment_file);
                            onVideoReload(result.updatedSegment);
                        }
                    });
                }
            }

            // Message de résumé
            if (failCount === 0) {
                setMessage(`✨ All ${successCount} segments synced successfully!`);
                setMessageType('success');
            } else if (successCount > 0) {
                setMessage(`⚠️ ${successCount} synced, ${failCount} failed`);
                setMessageType('warning');
            } else {
                setMessage('❌ All segments failed to sync');
                setMessageType('error');
            }

            // Réinitialiser la sélection
            setSelectedSegmentIndices(new Set());
        } catch (error) {
            setMessage('❌ Something went wrong. Please try again.');
            setMessageType('error');
        } finally {
            // Nettoyer après un délai
            setTimeout(() => {
                setProcessingSegments(new Map());
                setOverallProgress(0);
            }, 2000);
        }
    };

    // Calculer la progression globale
    useEffect(() => {
        if (processingSegments.size > 0) {
            const totalProgress = Array.from(processingSegments.values())
                .reduce((sum, { progress }) => sum + progress, 0);
            setOverallProgress(totalProgress / processingSegments.size);
        } else {
            setOverallProgress(0);
        }
    }, [processingSegments]);

    // Auto-hide messages
    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => {
                setMessage('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    const isProcessing = processingSegments.size > 0;

    // Styles
    const styles = {
        container: {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#1e1e1e',
            padding: '24px'
        },
        header: {
            marginBottom: '32px'
        },
        title: {
            color: 'white',
            fontSize: '24px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px'
        },
        subtitle: {
            color: 'rgba(255,255,255,0.6)',
            fontSize: '14px'
        },
        selectionInfo: {
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
        },
        chip: {
            backgroundColor: PRIMARY_COLOR,
            color: 'white',
            padding: '4px 12px',
            borderRadius: '16px',
            fontSize: '12px',
            fontWeight: '500'
        },
        selectAllButton: {
            background: 'none',
            border: 'none',
            color: PRIMARY_COLOR,
            fontSize: '14px',
            cursor: 'pointer',
            padding: '4px 8px',
            borderRadius: '4px',
            transition: 'background-color 0.3s'
        },
        progressBar: {
            marginBottom: '24px',
            height: '6px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            borderRadius: '3px',
            overflow: 'hidden'
        },
        progressFill: {
            height: '100%',
            backgroundColor: PRIMARY_COLOR,
            borderRadius: '3px',
            transition: 'width 0.3s ease'
        },
        alert: {
            marginBottom: '24px',
            padding: '12px 16px',
            borderRadius: '8px',
            backgroundColor: 'rgba(255,255,255,0.05)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        alertSuccess: {
            color: '#4caf50'
        },
        alertError: {
            color: '#ff5252'
        },
        alertWarning: {
            color: '#ff9800'
        },
        alertInfo: {
            color: '#2196f3'
        },
        segmentsList: {
            flexGrow: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingRight: '8px'
        },
        emptyState: {
            textAlign: 'center',
            paddingTop: '64px',
            paddingBottom: '64px',
            color: 'rgba(255,255,255,0.4)'
        },
        segmentCard: {
            padding: '20px',
            marginBottom: '12px',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
        },
        checkbox: {
            width: '18px',
            height: '18px',
            borderRadius: '4px',
            border: '2px solid rgba(255,255,255,0.3)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'transparent',
            transition: 'all 0.2s'
        },
        checkboxChecked: {
            backgroundColor: PRIMARY_COLOR,
            borderColor: PRIMARY_COLOR
        },
        iconCircle: {
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        segmentContent: {
            flexGrow: 1
        },
        segmentTime: {
            color: 'white',
            fontWeight: '500',
            fontSize: '14px',
            marginBottom: '4px'
        },
        segmentText: {
            color: 'rgba(255,255,255,0.6)',
            fontSize: '13px'
        },
        statusBadge: {
            fontSize: '12px',
            fontWeight: '500'
        },
        actionButton: {
            width: '100%',
            marginTop: '24px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '600',
            borderRadius: '8px',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
        },
        loadingSpinner: {
            display: 'inline-block',
            width: '20px',
            height: '20px',
            border: '2px solid rgba(255,255,255,0.3)',
            borderTopColor: 'white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
        }
    };

    return (
        <div style={styles.container}>
            <style>{`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                
                .segment-card:hover {
                    background-color: rgba(255,255,255,0.06) !important;
                    border-color: rgba(255,255,255,0.15) !important;
                }
                
                .segment-card-selected {
                    background-color: rgba(99, 102, 241, 0.15) !important;
                    border: 2px solid ${PRIMARY_COLOR} !important;
                }
                
                .segment-card-selected:hover {
                    background-color: rgba(99, 102, 241, 0.2) !important;
                }
                
                .scrollbar::-webkit-scrollbar {
                    width: 8px;
                }
                
                .scrollbar::-webkit-scrollbar-track {
                    background-color: transparent;
                }
                
                .scrollbar::-webkit-scrollbar-thumb {
                    background-color: rgba(255,255,255,0.2);
                    border-radius: 4px;
                }
                
                .scrollbar::-webkit-scrollbar-thumb:hover {
                    background-color: rgba(255,255,255,0.3);
                }
            `}</style>

            {/* Header */}
            <div style={styles.header}>
                <h1 style={styles.title}>
                    <SyncIcon />
                    Lip Sync
                </h1>
                <p style={styles.subtitle}>
                    Select video segments to sync with their audio
                </p>
            </div>

            {/* Selection info */}
            {selectedSegmentIndices.size > 0 && (
                <div style={styles.selectionInfo}>
                    <span style={styles.chip}>
                        {selectedSegmentIndices.size} selected
                    </span>
                    {videoAudioSegments.length > 1 && (
                        <button
                            style={styles.selectAllButton}
                            onClick={handleSelectAll}
                            disabled={isProcessing}
                        >
                            {selectedSegmentIndices.size === videoAudioSegments.length ?
                                'Deselect all' : 'Select all'}
                        </button>
                    )}
                </div>
            )}

            {/* Progress bar */}
            {isProcessing && (
                <div style={styles.progressBar}>
                    <div style={{
                        ...styles.progressFill,
                        width: `${overallProgress}%`
                    }} />
                </div>
            )}

            {/* Messages */}
            {message && (
                <div style={{
                    ...styles.alert,
                    ...(messageType === 'success' && styles.alertSuccess),
                    ...(messageType === 'error' && styles.alertError),
                    ...(messageType === 'warning' && styles.alertWarning),
                    ...(messageType === 'info' && styles.alertInfo)
                }}>
                    {message}
                </div>
            )}

            {/* Segments List */}
            <div style={styles.segmentsList} className="scrollbar">
                {videoAudioSegments.length === 0 ? (
                    <div style={styles.emptyState}>
                        <SyncIcon />
                        <p style={{ marginTop: '16px', fontSize: '16px' }}>
                            No video segments available
                        </p>
                        <p style={{ marginTop: '8px', fontSize: '14px' }}>
                            Upload a video with audio to get started
                        </p>
                    </div>
                ) : (
                    <div>
                        {videoAudioSegments.map((segment, index) => {
                            const isSelected = selectedSegmentIndices.has(index);
                            const processingInfo = processingSegments.get(index);
                            const isSegmentProcessing = !!processingInfo;

                            return (
                                <div
                                    key={`${segment.segment_file}-${index}`}
                                    className={`segment-card ${isSelected ? 'segment-card-selected' : ''}`}
                                    style={{
                                        ...styles.segmentCard,
                                        backgroundColor: isSelected ?
                                            'rgba(99, 102, 241, 0.15)' :
                                            'rgba(255,255,255,0.03)',
                                        border: isSelected ?
                                            `2px solid ${PRIMARY_COLOR}` :
                                            '2px solid rgba(255,255,255,0.08)',
                                        opacity: isProcessing && !isSegmentProcessing ? 0.5 : 1,
                                        cursor: isProcessing && !isSegmentProcessing ? 'not-allowed' : 'pointer'
                                    }}
                                    onClick={() => handleSegmentToggle(index)}
                                >
                                    {/* Checkbox */}
                                    <div
                                        style={{
                                            ...styles.checkbox,
                                            ...(isSelected && styles.checkboxChecked)
                                        }}
                                    >
                                        {isSelected && (
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                                            </svg>
                                        )}
                                    </div>

                                    {/* Icon */}
                                    <div style={{
                                        ...styles.iconCircle,
                                        backgroundColor: isSegmentProcessing ? '#ff9800' :
                                            segment.lipsync_processed ? '#4caf50' :
                                                isSelected ? PRIMARY_COLOR :
                                                    'rgba(255,255,255,0.1)'
                                    }}>
                                        {isSegmentProcessing ? (
                                            <div style={styles.loadingSpinner} />
                                        ) : segment.lipsync_processed || processingInfo?.status === 'success' ? (
                                            <CheckCircleIcon />
                                        ) : (
                                            <PlayCircleIcon />
                                        )}
                                    </div>

                                    {/* Content */}
                                    <div style={styles.segmentContent}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={styles.segmentTime}>
                                                {formatTime(segment.start)} - {formatTime(segment.end)}
                                            </div>
                                            {(segment.lipsync_processed || processingInfo?.status === 'success') && (
                                                <span style={{ ...styles.statusBadge, color: '#4caf50' }}>
                                                    Synced ✓
                                                </span>
                                            )}
                                            {processingInfo?.status === 'error' && (
                                                <span style={{ ...styles.statusBadge, color: '#ff5252' }}>
                                                    Failed ✗
                                                </span>
                                            )}
                                        </div>
                                        <div style={styles.segmentText}>
                                            {truncateText(segment.text) || 'No dialogue'}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Action Button */}
            {videoAudioSegments.length > 0 && (
                <button
                    style={{
                        ...styles.actionButton,
                        backgroundColor: selectedSegmentIndices.size === 0 ?
                            'rgba(255,255,255,0.1)' : PRIMARY_COLOR,
                        boxShadow: selectedSegmentIndices.size === 0 ? 'none' :
                            '0 4px 12px rgba(99, 102, 241, 0.3)',
                        cursor: selectedSegmentIndices.size === 0 || isProcessing ?
                            'not-allowed' : 'pointer',
                        opacity: selectedSegmentIndices.size === 0 || isProcessing ? 0.6 : 1
                    }}
                    onClick={handleLipSync}
                    disabled={selectedSegmentIndices.size === 0 || isProcessing}
                    onMouseEnter={(e) => {
                        if (selectedSegmentIndices.size > 0 && !isProcessing) {
                            e.target.style.backgroundColor = SECONDARY_COLOR;
                            e.target.style.boxShadow = '0 6px 20px rgba(99, 102, 241, 0.4)';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (selectedSegmentIndices.size > 0 && !isProcessing) {
                            e.target.style.backgroundColor = PRIMARY_COLOR;
                            e.target.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.3)';
                        }
                    }}
                >
                    {isProcessing ? (
                        <>
                            <div style={styles.loadingSpinner} />
                            Processing {processingSegments.size} segments...
                        </>
                    ) : (
                        <>
                            <SyncIcon />
                            {selectedSegmentIndices.size === 0 ?
                                'Select segments' :
                                `Sync ${selectedSegmentIndices.size} segment${selectedSegmentIndices.size > 1 ? 's' : ''}`
                            }
                        </>
                    )}
                </button>
            )}
        </div>
    );
}

export default LipSyncManager;